<script setup>
// Inline warning banner shown above a check-in CTA when availability is
// blocked (blackout day or frequency cap reached).
// Port of `BlockedNotice` in the source `_shared/CheckInSheet.tsx`.
import { AlertTriangle, Clock } from '@lucide/vue'

defineProps({
  availability: { type: Object, required: true },
})
</script>

<template>
  <div v-if="!availability.ok" class="cs-blocked">
    <AlertTriangle class="cs-blocked__icon" :size="14" />
    <div class="cs-blocked__text">
      <div class="cs-blocked__label">{{ availability.blockedLabel ?? 'Unavailable' }}</div>
      <div v-if="availability.reason" class="cs-blocked__reason">{{ availability.reason }}</div>
      <div v-if="availability.nextAvailableLabel" class="cs-blocked__next">
        <Clock :size="12" /> Next check-in {{ availability.nextAvailableLabel }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use './checkin-shared' as *;

// Source: `mb-2 flex items-start gap-2 rounded-xl bg-rose-500/10 px-3 py-2
//          text-rose-600 ring-1 ring-rose-500/30`
.cs-blocked {
  margin-bottom: 8px; // mb-2
  display: flex;
  align-items: flex-start;
  gap: 8px; // gap-2
  border-radius: 12px; // rounded-xl
  background: rgba($cs-rose-500, 0.1);
  padding: 8px 12px; // px-3 py-2
  color: $cs-rose-600;
  box-shadow: 0 0 0 1px rgba($cs-rose-500, 0.3); // ring-1 ring-rose-500/30

  &__icon {
    margin-top: 2px; // mt-0.5
    flex-shrink: 0;
  }

  &__text {
    font-size: 11px; // text-[11px]
    line-height: 1.375; // leading-snug
  }

  &__label {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em; // tracking-wider
  }

  &__reason {
    opacity: 0.9;
  }

  &__next {
    margin-top: 2px; // mt-0.5
    display: inline-flex;
    align-items: center;
    gap: 4px; // gap-1
    opacity: 0.9;
  }
}
</style>
